import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Button } from "../f7";
import { Notice } from "./ui";

/**
 * The documented traps, one component each.
 *
 * Each of these explains something the API reference warns implementers about.
 * Written once and reused, because repeating an explanation on twelve pages is
 * how one page eventually forgets it.
 */

export function Loading({ what = "" }: { what?: string }) {
  return <Notice>Loading{what ? ` ${what}` : ""}…</Notice>;
}

/**
 * A 404 from a tenant route.
 *
 * NOT "this merchant does not exist". Merchants resolve from a verification
 * ledger whose rows carry a 365-day TTL, so a dormant merchant is absent while
 * being perfectly alive. Blocking works by reference regardless — which is why
 * this component takes `actions`: a page that still performs the one operation
 * that works on a 404 merchant is the honest version of this state.
 */
export function NotSeenRecently({
  reference,
  actions,
}: {
  reference: string;
  actions?: ReactNode;
}) {
  return (
    <Notice tone="amber" title="Not seen recently">
      <p>
        <span className="ref">{reference}</span> is not in the verification ledger,
        whose entries expire after a year. That does <strong>not</strong> mean the
        merchant is gone — a dormant merchant looks exactly like this.
      </p>
      {actions && (
        <div className="mt-3">
          <p className="mb-2 text-xs">
            Blocking works by reference and does not need the ledger, so it still
            applies here:
          </p>
          {actions}
        </div>
      )}
    </Notice>
  );
}

/**
 * A 503. The OPPOSITE of a 404 — we could not look.
 *
 * Never renders an empty table, and always offers a retry, because an empty
 * state here would read as "there is nothing" during a transient blip.
 */
export function CouldNotLook({
  what,
  message,
  onRetry,
}: {
  what: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <Notice tone="amber" title={`Could not read ${what}`}>
      <p>
        {message ? `${message} ` : ""}This is a lookup failure, not an empty result —
        nothing has been deleted or changed.
      </p>
      {onRetry && (
        <Button outline onClick={onRetry}
          className="mt-2">
          Try again
        </Button>
      )}
    </Notice>
  );
}

/** A 403. Distinct from a sign-in problem, and says what to ask for. */
export function Denied({ message }: { message: string }) {
  return (
    <Notice tone="amber" title="You do not have access to this">
      <p>{message}</p>
      <p className="mt-2 text-xs">
        <Link to="/governance/permissions" className="underline">
          See what your groups allow
        </Link>
      </p>
    </Notice>
  );
}

export function SignedOut() {
  return (
    <Notice tone="amber" title="Signed out">
      Your session ended. Reload the page to sign in again.
    </Notice>
  );
}

export function LoadFailed({ what, message }: { what: string; message: string }) {
  return (
    <Notice tone="red" title={`Could not load ${what}`}>
      {message}
    </Notice>
  );
}

/**
 * An empty cap map.
 *
 * `{}` means unlimited for every tool — the designed default, per
 * usage/quotas.py rule 4 — not a misconfiguration. Rendering an empty grid
 * would invite someone to "fix" a system that is working correctly.
 */
export function EmptyByDesign({ scope }: { scope: string }) {
  return (
    <Notice title="No cap rows">
      Every tool is unlimited for {scope}. An empty cap table is the designed
      default, not a misconfiguration — tools with no row are simply not metered.
    </Notice>
  );
}

/** A product the SPA knows about but this gateway has no route for. */
export function NotMounted({ product }: { product: string }) {
  return (
    <Notice title={`${product} is not mounted here`}>
      No <span className="ref">lambda_arn</span> is published for this environment, so
      the gateway skipped its routes. That is expected for a product that has not
      deployed yet — it is not an outage.
    </Notice>
  );
}
